import {
  CustomLayoutDrawer,
  CustomLayoutDrawerContent,
  DefaultCustomLayoutDrawerProps,
} from 'module/ui/components/atoms/drawer';
import { FontAwesomeIcon } from 'module/ui/components/atoms/icon/icon';
import { Heading03, Short01 } from 'module/ui/components/styles';
import { faTrashCan, faXmark } from '@fortawesome/pro-regular-svg-icons';
import { Button } from '@rocketlane/ui';
import { PageType } from 'api/page-template/types';
import classNames from 'classnames';
import React, { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { DocxPreviewViewer, isDocxFile } from './docx-preview-viewer';
import { getIconForAiPageType, getIconForFileType } from './icon-for-file-type';
import { ImageViewer } from './image-viewer';
import { PDFViewer } from './pdf-viewer';
import { KnowledgeBaseFileViewerTab, KnowledgeBaseFileViewerTabs } from './tab';
import { TemplateViewer } from './template-viewer';
import { TextViewer } from './text-viewer';
import { UnsupportedFileFallback } from './unsupported-file-fallback';
import { FolderItem } from '../folder-content/types';

export const drawerContentWrapperTwClasses = () =>
  classNames(
    '[&_.ant-drawer-content-wrapper]:[box-shadow:var(--shadow-16)]',
    "[&_.ant-drawer-content-wrapper]:before:content-['']",
    '[&_.ant-drawer-content-wrapper]:before:fixed',
    '[&_.ant-drawer-content-wrapper]:before:top-0',
    '[&_.ant-drawer-content-wrapper]:before:bottom-0',
    '[&_.ant-drawer-content-wrapper]:before:!left-0',
    '[&_.ant-drawer-content-wrapper]:before:w-[100dvw]',
    '[&_.ant-drawer-content-wrapper]:before:pointer-events-none',
    '[&_.ant-drawer-content-wrapper]:before:-z-[1]',
    '[&_.ant-drawer-content-wrapper]:before:transition-colors',
    '[&_.ant-drawer-content-wrapper]:before:ease-in-out',
    '[&_.ant-drawer-content-wrapper]:before:duration-[var(--animation-duration-medium)]',
    '[&_.ant-drawer-content-wrapper]:before:!bg-black/10',
    '[&_.ant-drawer-content-wrapper]:hover:before:!bg-black/30',
  );

const ViewerHeader = styled.div`
  display: flex;
  padding: var(--spacing-05) var(--spacing-06);
  border-bottom: 1px solid var(--divider-default);
  justify-content: space-between;
`;
const ViewerTitleWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;
const ViewerTitle = styled.h3`
  ${Heading03};
  display: flex;
  align-items: center;
  gap: var(--spacing-03);
`;
const ViewerTitleSubtitle = styled.p`
  ${Short01};
  color: var(--text-secondary);
`;

const ViewerActions = styled.div`
  display: flex;
  gap: 12px;
`;

const ViewerContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-03);
  padding: var(--spacing-05) var(--spacing-06) 0;
  height: 100%;
  overflow: hidden;
`;
const FileView = styled.div`
  height: 100%;
  width: 100%;
  overflow: auto;
  padding-bottom: 80px;
`;

interface FileViewerProps {
  file: FolderItem;
  onClose: () => void;
  onDownload?: () => void;
  onDelete?: () => void;
}

const getFileType = (fileName: string): string => {
  const extension = fileName.split('.').pop()?.toLowerCase();

  switch (extension) {
    case 'pdf':
      return 'pdf';
    case 'png':
    case 'jpg':
    case 'jpeg':
    case 'gif':
    case 'bmp':
    case 'svg':
    case 'webp':
      return 'image';
    case 'md':
    case 'markdown':
      return 'markdown';
    case 'txt':
    case 'json':
    case 'csv':
    case 'tsv':
    case 'js':
    case 'ts':
    case 'jsx':
    case 'tsx':
    case 'css':
    case 'html':
    case 'htm':
    case 'xml':
    case 'yaml':
    case 'yml':
      return 'text';
    case 'doc':
    case 'docx':
    case 'xls':
    case 'xlsx':
    case 'ppt':
    case 'pptx':
      return 'office';
    default:
      return 'unknown';
  }
};

export const KnowledgeBaseFileViewer: React.FC<FileViewerProps> = ({
  file,
  onClose,
  onDownload,
  onDelete,
}) => {
  const fileType = useMemo(() => {
    if (
      file.actualContentType === 'AI_PAGE' ||
      file.actualContentType === 'WEBSITE'
    ) {
      return file.actualContentType;
    }

    return getFileType(file.name);
  }, [file.actualContentType, file.name]);

  const handleDownload = () => {
    if (onDownload) {
      onDownload();
    } else {
      const link = document.createElement('a');

      link.href = file.url!;
      link.download = file.name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const renderContent = () => {
    switch (fileType) {
      case 'pdf':
      case 'WEBSITE':
        return <PDFViewer url={file.url!} fileName={file.name} />;

      case 'image':
        return <ImageViewer url={file.url!} name={file.name} />;

      case 'text':
        return <TextViewer url={file.url!} fileType="text" />;
      case 'markdown':
        return <TextViewer url={file.url!} fileType="markdown" />;

      case 'office':
        if (file.url && isDocxFile(file.name)) {
          return (
            <DocxPreviewViewer
              fileUrl={file.url}
              fileName={file.name}
              onDownload={handleDownload}
            />
          );
        }

        return (
          <UnsupportedFileFallback
            fileName={file.name}
            onDownload={handleDownload}
            message={
              <FormattedMessage id="knowledge_base.file_viewer.office_cannot_preview" />
            }
            hint={
              <FormattedMessage id="knowledge_base.file_viewer.office_download_hint" />
            }
          />
        );

      case 'AI_PAGE':
        return file.template ? (
          <TemplateViewer template={file.template} />
        ) : null;

      default:
        return (
          <UnsupportedFileFallback
            fileName={file.name}
            onDownload={handleDownload}
            message={
              <FormattedMessage id="knowledge_base.file_viewer.type_not_supported" />
            }
            hint={
              <FormattedMessage id="knowledge_base.file_viewer.download_to_view" />
            }
          />
        );
    }
  };
  const icon = useMemo(() => {
    if (file.actualContentType === 'AI_PAGE') {
      return getIconForAiPageType(file.contentType as PageType);
    }

    return getIconForFileType(file.name);
  }, [file.actualContentType, file.contentType, file.name]);
  const formattedFileSize = useMemo(() => {
    if (file.originalContent?.contentType !== 'FILE') {
      return undefined;
    }

    const bytes = file.originalContent.attachment?.sizeInBytes;

    if (bytes === 0) {
      return '0 B';
    }

    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    const value = bytes / Math.pow(k, i);

    return `${value % 1 === 0 ? value : value.toFixed(1)} ${sizes[i]}`;
  }, [file.originalContent]);

  return (
    <CustomLayoutDrawer
      {...DefaultCustomLayoutDrawerProps}
      open={true}
      onClose={onClose}
      width={'70%'}
      closeIcon={<></>}
      maskClosable={false}
      mask={false}
      className={classNames(drawerContentWrapperTwClasses())}
    >
      <CustomLayoutDrawerContent>
        <ViewerHeader>
          <ViewerTitleWrapper>
            <ViewerTitle>
              <FontAwesomeIcon icon={icon} /> {file.name}
            </ViewerTitle>
            {formattedFileSize ? (
              <ViewerTitleSubtitle>{formattedFileSize}</ViewerTitleSubtitle>
            ) : null}
          </ViewerTitleWrapper>
          <ViewerActions>
            {onDelete && (
              <Button
                variant="secondary"
                leftIcon={<FontAwesomeIcon icon={faTrashCan} size={12} />}
                onClick={onDelete}
              />
            )}
            <Button
              variant="secondary"
              leftIcon={<FontAwesomeIcon icon={faXmark} size={12} />}
              onClick={onClose}
            />
          </ViewerActions>
        </ViewerHeader>
        <ViewerContent>
          <KnowledgeBaseFileViewerTabs
            activeTab={KnowledgeBaseFileViewerTab.Preview}
            onChange={() => {}}
          />
          <FileView>{renderContent()}</FileView>
        </ViewerContent>
      </CustomLayoutDrawerContent>
    </CustomLayoutDrawer>
  );
};
